import { Button, Toast, useToast } from '@zillow/constellation-10';

import { getBooleanConfig } from '@zg-rentals/frontend-config/client';
import {
  useUndoMarkOneTimePaymentAsPaidOfflineWebApiHandler_undoMarkOneTimePaymentAsPaidOffline,
  useUndoMarkPaymentAsPaidOfflineWebApiHandler_undoMarkPaymentAsPaidOffline,
} from '@zg-rentals/ts-rent-payment-client-react-query';

import { getIreneDashboard } from '@rental-manager/actions/requests';
import { logError } from '@zg-rentals/log-error';
import { useAppDispatch } from '../../../../../../../../store/hooks';

type UndoTarget =
  | { kind: 'rent'; rentTermsKey: string; scheduleMonth: string }
  | { kind: 'oneTimePayment'; groupKey: string };

type Props = {
  propertyId: string;
  notes?: string;
  target: UndoTarget;
};

const UndoOfflineMarkAction = ({ propertyId, notes, target }: Props) => {
  const dispatch = useAppDispatch();
  const { enqueueToast } = useToast();
  const rentMutation = useUndoMarkPaymentAsPaidOfflineWebApiHandler_undoMarkPaymentAsPaidOffline({
    meta: {
      invalidates: ['landlordTransactions'],
      context: {
        api: 'UndoMarkPaymentAsPaidOfflineWebApiHandler_undoMarkPaymentAsPaidOffline',
        details: 'single-property dashboard undo offline mark',
      },
    },
  });
  const oneTimePaymentMutation =
    useUndoMarkOneTimePaymentAsPaidOfflineWebApiHandler_undoMarkOneTimePaymentAsPaidOffline({
      meta: {
        invalidates: ['landlordTransactions'],
        context: {
          api: 'UndoMarkOneTimePaymentAsPaidOfflineWebApiHandler_undoMarkOneTimePaymentAsPaidOffline',
          details: 'single-property dashboard undo offline mark',
        },
      },
    });
  const isPending = rentMutation.isPending || oneTimePaymentMutation.isPending;

  if (!getBooleanConfig('isLandlordMarkAsPaidOfflineEnabled', false)) {
    return null;
  }

  const handleUndo = () => {
    const callbacks = {
      onSuccess: () => {
        // The single-property ledger is Redux-driven (not a React Query cache), so reload it
        // to revert the row and restore the renter's past-due status.
        dispatch(getIreneDashboard({ propertyId })).catch(logError);
        enqueueToast(<Toast tone="success">Recorded payment undone</Toast>);
      },
      onError: () => {
        enqueueToast(<Toast tone="critical">Payment couldn’t be removed. Try again.</Toast>);
      },
    };
    if (target.kind === 'rent') {
      rentMutation.mutate(
        {
          postData: {
            notes: notes ?? '',
            rentTermsKey: target.rentTermsKey,
            resourceId: propertyId,
            scheduleMonthForPayment: target.scheduleMonth,
          },
        },
        callbacks,
      );
    } else {
      oneTimePaymentMutation.mutate(
        {
          postData: {
            notes: notes ?? '',
            groupKey: target.groupKey,
          },
        },
        callbacks,
      );
    }
  };

  return (
    <Button size="sm" disabled={isPending} onClick={handleUndo}>
      Undo
    </Button>
  );
};

export default UndoOfflineMarkAction;
